/**
 * Footer Component
 *
 * Renders the main footer for the Andoria AI website.
 * Features:
 * - Responsive layout with background image and gradient overlay
 * - Company logo, navigation links, legal links, and contact info
 * - Copyright section
 *
 * @returns {JSX.Element} The site footer with branding and navigation
 */
'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';

/**
 * Main Footer functional component.
 * Contains branding, navigation, legal, and contact sections.
 */
export default function Footer() {
  /**
   * Main navigation links for the footer.
   * Each link has a title and a URL.
   */
  const pageLinks = [
    { title: 'Home', url: '/' },
    { title: 'About Us', url: '/about' },
    { title: 'Services', url: './services' },
    { title: 'Blog', url: './blog' },
    { title: 'Events', url: './events' },
    { title: 'FAQs', url: './#faq' },
    { title: 'Contact Us', url: '/#contact' },
  ];

  /**
   * Legal links for privacy and terms.
   */
  const legalLinks = [
    { title: 'Privacy Policy', url: '#' },
    { title: 'Terms of Service', url: '#' },
  ];

  return (
    <footer className="w-full gradient-bg text-white relative overflow-hidden">
      {/*
        Background image: Large, low-opacity ANDORIA text for branding.
        Positioned absolutely for visual effect, not interactive.
      */}
      <div className="absolute top-[90%] left-[-10%] w-[120%] sm:top-[25%] sm:left-[-10%] sm:w-[140%] md:top-[73%] md:left-[-50px] md:w-[115%] lg:top-[285px] lg:left-[-110px] lg:w-[1680px] z-0">
        <Image
          src="/images/logo-andoria-white.png" // Large ANDORIA text image
          alt="ANDORIA AI background text"
          width={2000}
          height={263}
          className="opacity-[0.05] object-contain"
          priority
        />
      </div>

      {/* Main content container for footer sections */}
      <div className="container mx-auto px-6 py-16 relative z-10">
        <div className="flex flex-col lg:flex-row justify-between items-start mb-16">
          {/* Logo section */}
          <div className="mb-12 lg:mb-0">
            <Image
              src="/images/logo-andoria-white.png"
              className="h-8 w-auto brightness-0 invert"
              alt="Androia AI Logo"
              width={150.32772827148438}
              height={25}
            />
          </div>

          {/* Legal and Contact columns */}
          <div className="flex flex-col md:flex-row gap-12 md:gap-20">
            {/* Legal links column */}
            <div className="flex flex-col space-y-4">
              <h4 className="text-base font-semibold opacity-90 mb-2">Legal</h4>
              {legalLinks.map((link, index) => (
                <Link
                  key={index}
                  href={link.url}
                  className="text-lg font-normal hover:opacity-80 transition-opacity"
                >
                  {link.title}
                </Link>
              ))}
            </div>

            {/* Contact information column */}
            <div className="flex flex-col space-y-4">
              <h4 className="text-base font-semibold opacity-90 mb-2">Contact Information</h4>
              <a
                href="mailto:info@andoriaai.com"
                className="text-lg font-normal hover:opacity-80 transition-opacity"
              >
                Email: info@andoriaai.com
              </a>
            </div>
          </div>
        </div>

        {/* Navigation links section */}
        <div className="grid grid-cols-2 gap-4 mb-12 py-10 border-t border-white/20 sm:flex sm:flex-wrap sm:justify-center sm:gap-10">
          {pageLinks.map((link, index) => (
            <Link
              key={index}
              href={link.url}
              className="text-lg font-normal hover:opacity-80 transition-opacity text-center sm:text-left"
            >
              {link.title}
            </Link>
          ))}
        </div>

        {/* Copyright and year */}
        <div className="flex items-center justify-center gap-3 pt-10 border-t border-white/20">
          <div className="flex items-center gap-2">
            <div className="relative w-3 h-3">
              <Image src="/images/img_copyright.svg" alt="Copyright" width={12} height={12} />
            </div>
            <span className="font-normal text-sm">Andoria AI</span>
            <span className="font-normal text-sm">2025</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
